import { useEffect } from 'react';
import { Users, TrendingUp, Heart, Shield, Linkedin } from 'lucide-react';
import { BrandHero, FeatureGrid, CtaBand, SectionHeader } from '@/components/brand/BrandKit';
import { JobBoard } from '@/components/careers/JobBoard';
import { LinkedInFeed } from '@/components/LinkedInFeed';
import { cultureLinkedinPosts } from '@/data/linkedinPosts';
import { CAREERS_EMAIL, LINKEDIN_COMPANY_URL, applyMailto } from '@/data/jobs';

const HERO_IMG = "/generated/about-group-cityscape.jpg";

const benefits = [
  { icon: TrendingUp, title: "Growth & Development", desc: "Structured career paths, the Leadership Development Programme, and cross-functional exposure across 6 business divisions." },
  { icon: Users, title: "Team of 1,200+", desc: "Join a diverse, professional team that has been moving the UAE forward since 1976." },
  { icon: Heart, title: "People-First Culture", desc: "Built on our F.A.I.R. values: Fearless, Accountable, Innovative, Respectful." },
  { icon: Shield, title: "Stability & Heritage", desc: "50 years of operational excellence and a fleet of 12,000+ vehicles across the UAE." },
];

export default function CareersPage() {
  useEffect(() => { document.title = "Careers | Open Roles at Eurogulf Mobility Group | Dubai, UAE"; }, []);

  return (
    <div data-testid="careers-page">
      <BrandHero testid="careers-hero" chip="We're Hiring" title="Grow your career with the group that keeps the UAE moving."
        lead="Join a group that keeps people, businesses and operations moving. From airport counters and workshops to data, finance and sales, our open roles span every part of the mobility business."
        image={HERO_IMG} imageAlt="Eurogulf Mobility Group team against the Dubai skyline" position="center 40%"
        ctas={[{ label: 'View Open Positions', href: '#openings', testid: 'careers-view-openings' }, { label: 'Follow on LinkedIn', href: LINKEDIN_COMPANY_URL, external: true, variant: 'outline' }]}>
        <p className="mt-4 inline-flex items-center gap-2 font-body text-xs text-white/60"><Linkedin className="w-3.5 h-3.5" /> Life at Eurogulf, on LinkedIn</p>
      </BrandHero>

      <FeatureGrid testid="careers-benefits" chip="Why Eurogulf Mobility Group" title="Be part of a legacy shaping UAE mobility." desc="Be part of a legacy that has been shaping the UAE's mobility landscape for five decades." items={benefits} cols={4} />

      <section id="openings" data-testid="careers-listings" className="bg-[#FAFAFA] border-y border-black/5 py-14 sm:py-20 scroll-mt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader compact chip="Open Positions" title="Find your next role." align="center" />
          <div className="mt-8"><JobBoard lang="en" /></div>
        </div>
      </section>

      <LinkedInFeed posts={cultureLinkedinPosts} lang="en" eyebrow="Life at Eurogulf" title="Culture, Learning and Recognition" testId="careers-culture-feed" />

      <CtaBand title="Don't see your role?" desc={`Send your CV to ${CAREERS_EMAIL} and we will keep you in mind for future opportunities across the group.`}
        ctas={[{ label: 'Send Your CV', href: applyMailto("General Application"), mailto: true, testid: 'careers-send-cv' }]} />
    </div>
  );
}
